/**
 * Simple eSIM Checkout Validator
 * Direct onclick handler approach - no complex event binding
 */

// Debug logging helper
function debugLog(...args) {
  if (window.ESimCheckoutValidator_debugMode !== undefined) {
    // Use theme setting value
    if (window.ESimCheckoutValidator_debugMode) {
      console.log(...args);
    }
  } else {
    // Fallback: check if debug mode is enabled via URL parameter or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const urlDebug = urlParams.get('esim_debug') === 'true';
    const localDebug = localStorage.getItem('ESimCheckoutValidator_debugMode') === 'true';
    
    if (urlDebug || localDebug) {
      console.log(...args);
    }
  }
}

// Global validation function that can be called directly from onclick
window.validateESimCheckout = async function() {
  // debugLog('🚀 ESimCheckoutValidator: Direct validation called');
  
  try {
    // Get EliteMobile data calls SMS eSIM products from cart
    const dataCallsSmsProducts = await getDataCallsSmsESimProducts();
    // debugLog(`📦 Found ${dataCallsSmsProducts.length} EliteMobile data calls SMS eSIM products in cart`);

    if (dataCallsSmsProducts.length === 0) {
      // debugLog('✅ No EliteMobile data calls SMS eSIM products, allowing checkout');
      return true; // Allow checkout to proceed
    }

    // Check availability for each data calls SMS product
    // debugLog('🔍 Checking product availability...');
    const unavailableProducts = await checkProductsAvailability(dataCallsSmsProducts);
    
    if (unavailableProducts.length > 0) {
      // debugLog(`❌ Found ${unavailableProducts.length} unavailable products`);
      showUnavailableProductsModal(unavailableProducts);
      return false; // Block checkout
    } else {
      // debugLog('✅ All products available, allowing checkout');
      return true; // Allow checkout to proceed
    }
  } catch (error) {
    console.error('❌ Error during validation:', error);
    return true; // In case of error, allow checkout to proceed
  }
};

async function getDataCallsSmsESimProducts() {
  // debugLog('📋 Getting EliteMobile data calls SMS eSIM products from cart...');
  
  try {
    // Fetch current cart data
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    // Fetch all products data
    const productsResponse = await fetch('/products.json');
    const productsData = await productsResponse.json();
    
    // debugLog(`🛒 Cart has ${cart.items.length} items`);
    // debugLog(`📦 Found ${productsData.products.length} products in store`);
    
    const dataCallsSmsProducts = [];
    
    for (const item of cart.items) {
      // debugLog(`\n🔍 CHECKING: ${item.product_title}`);
      // debugLog(`📄 SKU: ${item.sku || 'No SKU'}`);
      
      // Find product details
      const product = productsData.products.find(p => p.id === item.product_id);
      if (!product) {
        // debugLog(`❌ Product not found in products.json`);
        continue;
      }
      
      // debugLog(`📝 Product Type: ${product.product_type || 'undefined'}`);
      // debugLog(`🏷️ Tags: [${product.tags ? product.tags.join(', ') : 'none'}]`);
      // debugLog(`🏢 Vendor: ${product.vendor || 'undefined'}`);
      
      // Check if product is eSIM type
      const isESimType = product.product_type === 'eSIM';
      
      // Check if has data-calls-sms tag
      const hasDataCallsSmsTag = product.tags && product.tags.includes('data-calls-sms');
      
      // Check if vendor is EliteMobile
      const isEliteMobileVendor = product.vendor === 'EliteMobile';
        
      // debugLog(`🎯 eSIM=${isESimType}, Data, Calls & SMS=${hasDataCallsSmsTag}, EliteMobile=${isEliteMobileVendor}`);
        
      if (isESimType && hasDataCallsSmsTag && isEliteMobileVendor) {
                    // debugLog(`✅ ELITE MOBILE DATA CALLS SMS eSIM FOUND: ${item.product_title}`);
                      dataCallsSmsProducts.push({
          id: item.id,
          variant_id: item.variant_id,
          product_id: item.product_id,
          product_title: item.product_title,
          product_handle: item.handle,
          quantity: item.quantity,
          line_index: cart.items.indexOf(item) + 1,
          sku: item.sku,
          product_type: product.product_type,
          vendor: product.vendor,
          tags: product.tags
        });
              } else {
          // debugLog(`⏭️ Not EliteMobile data calls SMS eSIM: ${item.product_title}`);
        }
    }
    
    return dataCallsSmsProducts;
  } catch (error) {
    console.error('❌ Error fetching EliteMobile data calls SMS eSIM products:', error);
    throw error;
  }
}

async function checkProductsAvailability(products) {
  // debugLog(`🔍 Checking availability for ${products.length} products...`);
  const unavailableProducts = [];
  
  if (products.length === 0) {
    return unavailableProducts;
  }
  
  try {
    // Call provider API once to get all available bundles
    // debugLog('📡 Calling provider API once for all products...');
    const availableBundles = await getAvailableBundles();
    
    if (!availableBundles) {
      // debugLog('❌ Failed to get available bundles, marking all as unavailable');
      return products; // All products unavailable if API fails
    }
    
    // debugLog(`📦 Got ${availableBundles.length} available bundles from provider`);
    availableBundles.forEach(bundle => {
      // debugLog(`   - ${bundle.BundleName} (Code: ${bundle.BundleProductCode})`);
    });
    
    // Check each product against the available bundles
    for (const product of products) {
      // debugLog(`\n🔍 Checking: ${product.product_title}`);
      
      if (!product.sku) {
        // debugLog(`❌ No SKU found for product`);
        unavailableProducts.push(product);
        continue;
      }
      
      // Extract SKU prefix (before first underscore)
      const skuPrefix = product.sku.split('_')[0];
      // debugLog(`🔍 SKU prefix: ${skuPrefix}`);
      
      // Check if SKU prefix matches any available bundle
      const isAvailable = availableBundles.some(bundle => 
        bundle.BundleProductCode === skuPrefix
      );
      
      if (isAvailable) {
        // debugLog(`✅ ${product.product_title}: Available`);
      } else {
        // debugLog(`❌ ${product.product_title}: Not available`);
        unavailableProducts.push(product);
      }
    }
    
    // debugLog(`📊 Result: ${unavailableProducts.length} out of ${products.length} products unavailable`);
    return unavailableProducts;
    
  } catch (error) {
    console.error('❌ Error during availability check:', error);
    // In case of error, mark all products as unavailable to be safe
    return products;
  }
}

async function getAvailableBundles() {
  // debugLog('🌐 Calling provider API to get available bundles...');
  
  try {
    // Call provider API
    const response = await fetch('https://hook.us2.make.com/2wedwcc04ydgs1h7xzyvc3qczr6nvch1', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const apiData = await response.json();
    // debugLog('📊 Provider API Response:', apiData);
    
    if (apiData.Status !== 1) {
      throw new Error(`API error: ${apiData.Message}`);
    }
    
    if (!apiData.BundleList || !Array.isArray(apiData.BundleList)) {
      throw new Error('Invalid BundleList in API response');
    }
    
    // debugLog(`✅ Successfully fetched ${apiData.BundleList.length} available bundles`);
    return apiData.BundleList;
    
  } catch (error) {
    console.error('❌ Provider API error:', error);
    return null;
  }
}

function showUnavailableProductsModal(unavailableProducts) {
  // debugLog('🚨 Showing unavailable products modal');
  
  const modalHTML = `
    <div id="esim-unavailable-modal" class="esim-modal-overlay">
      <div class="esim-modal">
        <div class="esim-modal-header">
          <h3>⚠️ Products Unavailable</h3>
          <button type="button" class="esim-modal-close" onclick="document.getElementById('esim-unavailable-modal').remove()">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="esim-modal-body">
          <p>The following eSIM products are currently out of stock and cannot be purchased:</p>
          <ul class="esim-unavailable-list">
            ${unavailableProducts.map(product => `
              <li class="esim-unavailable-item">
                <span class="esim-product-name">${product.product_title}</span>
                <button type="button" class="esim-remove-btn" onclick="removeProductFromCartOptimized(${product.line_index}, this)">
                  Remove from Cart
                </button>
              </li>
            `).join('')}
          </ul>
          <p>Please remove these products from your cart to continue with checkout.</p>
        </div>
        <div class="esim-modal-footer">
          <button type="button" class="esim-modal-close-btn" onclick="document.getElementById('esim-unavailable-modal').remove()">
            Close
          </button>
        </div>
      </div>
    </div>
  `;
  
  // Add modal styles if not already present
  if (!document.querySelector('#esim-modal-styles')) {
    const styles = `
      <style id="esim-modal-styles">
        .esim-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background-color: rgba(0, 0, 0, 0.5); display: flex;
          justify-content: center; align-items: center; z-index: 9999;
        }
        .esim-modal {
          background: white; border-radius: 8px; max-width: 500px; width: 90%;
          max-height: 80vh; overflow-y: auto; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .esim-modal-header {
          padding: 20px; border-bottom: 1px solid #eee; display: flex;
          justify-content: space-between; align-items: center;
        }
        .esim-modal-header h3 { margin: 0; color: #e74c3c; }
        .esim-modal-close {
          background: none; border: none; font-size: 24px; cursor: pointer; color: #999;
        }
        .esim-modal-body { padding: 20px; }
        .esim-unavailable-list { list-style: none; padding: 0; margin: 15px 0; }
        .esim-unavailable-item {
          display: flex; justify-content: space-between; align-items: center;
          padding: 10px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 10px;
        }
        .esim-product-name { font-weight: bold; }
        .esim-remove-btn {
          background-color: #e74c3c; color: white; border: none; padding: 5px 10px;
          border-radius: 4px; cursor: pointer; font-size: 12px;
        }
        .esim-remove-btn:hover { background-color: #c0392b; }
        .esim-modal-footer { padding: 20px; border-top: 1px solid #eee; text-align: right; }
        .esim-modal-close-btn {
          background-color: #95a5a6; color: white; border: none; padding: 10px 20px;
          border-radius: 4px; cursor: pointer;
        }
        .esim-modal-close-btn:hover { background-color: #7f8c8d; }
      </style>
    `;
    document.head.insertAdjacentHTML('beforeend', styles);
  }
  
  // Add modal to page
  document.body.insertAdjacentHTML('beforeend', modalHTML);
}

window.removeProductFromCartOptimized = async function(lineIndex, buttonElement) {
  // debugLog(`🗑️ Removing product from cart at line ${lineIndex}`);
  
  // Show loading state on button
  const originalText = buttonElement.innerHTML;
  buttonElement.innerHTML = '⏳ Removing...';
  buttonElement.disabled = true;
  
  try {
    // Use the same cart update mechanism as the existing cart
    const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    
    if (cartItems) {
      // debugLog('✅ Using existing cart system to remove product');
      
      // Call the existing updateQuantity method from cart.js
      cartItems.updateQuantity(lineIndex, 0);
      
      // Wait a moment for the cart to update
      setTimeout(() => {
        // Remove the item from the modal list
        const listItem = buttonElement.closest('.esim-unavailable-item');
        if (listItem) {
          listItem.remove();
        }
        
        // Check if modal is now empty
        const remainingItems = document.querySelectorAll('.esim-unavailable-item');
        if (remainingItems.length === 0) {
          // debugLog('✅ All unavailable products removed, closing modal');
          document.getElementById('esim-unavailable-modal').remove();
        }
      }, 1000);
      
    } else {
      // debugLog('⚠️ Cart system not found, using direct API call');
      
      // Fallback to direct API call
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: lineIndex, quantity: 0 })
      });
      
      if (response.ok) {
        // debugLog('✅ Product removed successfully via API');
        
        // Remove the item from the modal list
        const listItem = buttonElement.closest('.esim-unavailable-item');
        if (listItem) {
          listItem.remove();
        }
        
        // Check if modal is now empty
        const remainingItems = document.querySelectorAll('.esim-unavailable-item');
        if (remainingItems.length === 0) {
          // debugLog('✅ All unavailable products removed, closing modal');
          document.getElementById('esim-unavailable-modal').remove();
        }
        
        // Trigger cart update events
        const cartUpdateEvent = new CustomEvent('cart:update');
        document.dispatchEvent(cartUpdateEvent);
        
      } else {
        throw new Error(`Failed to remove product: ${response.status}`);
      }
    }
    
  } catch (error) {
    console.error('❌ Error removing product:', error);
    alert('Error removing product. Please try again.');
    
    // Restore button state
    buttonElement.innerHTML = originalText;
    buttonElement.disabled = false;
  }
};

// Global click handler for checkout buttons
window.handleESimCheckout = async function(button, formId) {
  // debugLog('🎯 Checkout button clicked:', button.id);
  // debugLog('📝 Form ID:', formId);
  
  // Show loading state
  button.disabled = true;
  const originalText = button.innerHTML;
  button.innerHTML = '⏳ Validating...';
  
  try {
    // Run validation
    const isValid = await validateESimCheckout();
    
    if (isValid) {
      // debugLog('✅ Validation passed, proceeding with checkout');
      
      // Redirect directly to checkout instead of submitting cart form
      // debugLog('🚀 Redirecting to checkout page');
      window.location.href = '/checkout';
    } else {
      // debugLog('❌ Validation failed, checkout blocked');
    }
  } catch (error) {
    console.error('❌ Error during checkout validation:', error);
    // Allow checkout on error - redirect to checkout
    // debugLog('⚠️ Error occurred, allowing checkout to proceed');
    window.location.href = '/checkout';
  } finally {
    // Restore button state
    button.disabled = false;
    button.innerHTML = originalText;
  }
};

    // debugLog('✅ Simple eSIM Checkout Validator loaded successfully');